import { useQuery } from '@tanstack/react-query';
import type { TPermissionSet, TPermission } from '../types';
import { AuthService } from '../services/AuthService';
import { Telemetry } from 'app/services/telemetry/Telemetry';

type SyncStorage = {
    getString(key: string): string | null;
    set(key: string, value: string): void;
    delete(key: string): void;
};

const isRemoteDebugging = () => (globalThis as any).nativeCallSyncHook == null;

let storage: SyncStorage | null = null;
function getStorage(): SyncStorage {
    if (storage) return storage;
    try {
        if (isRemoteDebugging()) throw new Error('remote debug -> fallback');
        const { MMKV } = require('react-native-mmkv');
        storage = new MMKV();
    } catch {
        const mem = new Map<string, string>();
        storage = {
            getString: k => mem.get(k) ?? null,
            set: (k, v) => void mem.set(k, v),
            delete: k => void mem.delete(k),
        };
        Telemetry.eventOnce('auth.storage.mmkv.disabled', { domain:'auth', fallback:'in_memory' }, 'warning');
    }
    return storage!;
}

const KEY = 'authz.permissions.v1';

function readFromStorage(): string[] {
    try {
        const raw = getStorage().getString(KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}
function writeToStorage(perms: string[]) {
    try {
        getStorage().set(KEY, JSON.stringify(perms));
    } catch { }
}
export function clearPermissionsCache() {
    try { getStorage().delete(KEY); } catch { }
}

export async function fetchPermissions(): Promise<string[]> {
    const res = await AuthService.getUserPermissions();
    const perms = res?.permissions ?? [];
    writeToStorage(perms);
    return perms;
}

export function usePermissions(opts?: { enabled?: boolean }) {
    const enabled = opts?.enabled ?? true;
    const data = useQuery({
        queryKey: ['authz', 'permissions'],
        queryFn: fetchPermissions,
        staleTime: 5 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        placeholderData: () => readFromStorage(),
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        enabled,
    });
    return data;
}

export function toSet(perms: string[]): TPermissionSet {
    return new Set(perms as TPermission[]);
}
